import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { errMsg } from "@/lib/api";
import { eur, fold } from "@/lib/format";
import { track } from "@/lib/analytics";
import { t } from "@/i18n";

export default function ImportDishesDialog({ analysis, open, onOpenChange, onDone }) {
  const dishes = analysis.menu.categories.flatMap((c) => c.dishes.filter((d) => d.price).map((d) => ({ ...d, category: c.name })));
  const [selected, setSelected] = useState(() => new Set(dishes.map((d) => d.name)));
  const [existing, setExisting] = useState(new Set());

  useEffect(() => {
    api.get("/recipes").then(({ data }) => {
      const names = new Set(data.map((r) => fold(r.name)));
      setExisting(names);
      setSelected((s) => new Set([...s].filter((n) => !names.has(fold(n)))));
    }).catch(() => {});
  }, []);
  const available = dishes.filter((d) => !existing.has(fold(d.name)));
  const [ttc, setTtc] = useState(true);
  const [busy, setBusy] = useState(false);
  const toggle = (n) => setSelected((s) => { const x = new Set(s); x.has(n) ? x.delete(n) : x.add(n); return x; });
  const ht = (p) => (ttc ? p / 1.1 : p);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/menu-analysis/${analysis.id}/import`, { prices_include_vat: ttc, vat_rate: 10, dishes: [...selected] });
      if (data.created > 0) track("menu_imported_to_account", { created: data.created });
      toast.success(t("importDishesDialog.x_plat_s_ajoute_s_a_vos_recettesx", { v0: data.created, v1: data.duplicates ? ` · ${data.duplicates} déjà présent(s)` : "" }));
      onDone(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="import-dishes-dialog">
        <DialogHeader>
          <DialogTitle>{t("importDishesDialog.ajouter_les_plats_a_mes_recettes")}</DialogTitle>
          <DialogDescription>{t("importDishesDialog.chaque_plat_devient_une_fiche_recette_avec")}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between gap-3 rounded-lg border p-3">
          <Label htmlFor="ttc" className="text-sm leading-snug">{t("importDishesDialog.prix_de_la_carte_ttc_tva_10")}<span className="block text-xs font-normal text-muted-foreground">{t("importDishesDialog.convertis_en_ht_pour_vos_calculs_de_marge")}</span></Label>
          <Switch id="ttc" checked={ttc} onCheckedChange={setTtc} data-testid="import-ttc-switch" />
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span data-testid="import-selected-count">{selected.size} / {available.length} {t("importDishesDialog.plats_selectionnes")}</span>
          {available.length > 0 && <button type="button" className="underline-offset-4 hover:underline" onClick={() => setSelected(selected.size === available.length ? new Set() : new Set(available.map((d) => d.name)))} data-testid="import-toggle-all-btn">
            {selected.size === available.length ? t("importDishesDialog.tout_deselectionner") : t("importDishesDialog.tout_selectionner")}
          </button>}
        </div>
        <ul className="max-h-[40vh] divide-y overflow-y-auto rounded-lg border">
          {dishes.map((d, i) => {
            const done = existing.has(fold(d.name));
            return (
            <li key={`${d.name}-${i}`}>
              <label className={`flex items-center gap-3 px-3 py-2.5 text-sm ${done ? "opacity-60" : "cursor-pointer"}`}>
                <Checkbox checked={!done && selected.has(d.name)} disabled={done} onCheckedChange={() => toggle(d.name)} data-testid={`import-dish-checkbox-${i}`} />
                <span className="min-w-0 flex-1"><span className="block truncate font-medium">{d.name}</span><span className="text-xs text-muted-foreground">{done ? <span data-testid={`import-dish-existing-${i}`}>{t("importDishesDialog.deja_dans_vos_recettes")}</span> : d.category}</span></span>
                <span className="num shrink-0 text-right text-xs">{eur(ht(d.price))} <span className="text-muted-foreground">{t("common.ht_short")}</span></span>
              </label>
            </li>
            );
          })}
        </ul>
        <DialogFooter>
          <Button onClick={submit} disabled={busy || !selected.size} className="w-full sm:w-auto" data-testid="import-dishes-confirm-btn">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{t("common.ajouter")} {t("common.n_dishes_paren", { v0: selected.size })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
